/**
 * IMPORTANT: Everything in this file is manually mirrored in sdk/pypi/seeq/spy/_shared_definition_constants.py.
 * Any additions, removals, or changes to this file should be duplicated in SPy.
 */
import moment from 'moment-timezone';
import type { BackwardsCompatibilityColumn } from '@/utilities/formula.constants';

// IMPORTANT: Any changes here must also update QUARTZ_CRON_PENDING_INPUT in _shared_definition_constants.py.
export const QUARTZ_CRON_PENDING_INPUT = '59 59 23 31 12 ? 2099';

// IMPORTANT: Any changes here must also update DURATION_SCALAR_UNITS in _shared_definition_constants.py.
export const DURATION_SCALAR_UNITS = [
  's',
  'second',
  'seconds',
  'min',
  'minute',
  'minutes',
  'h',
  'hr',
  'hrs',
  'hour',
  'hours',
  'day',
  'd',
  'days',
  'week',
  'wk',
  'weeks',
  'month',
  'mo',
  'months',
  'year',
  'y',
  'yr',
  'years',
  'ns',
  'ms',
];

// IMPORTANT: Any changes here must also update OFFSET_DIRECTION in _shared_definition_constants.py.
export enum OFFSET_DIRECTION {
  PAST = 'past',
  FUTURE = 'future',
}

// IMPORTANT: Any changes here must also update CAPSULE_SELECTION in _shared_definition_constants.py.
export const CAPSULE_SELECTION = {
  STRATEGY: {
    CLOSEST_TO: 'closestTo',
    OFFSET_BY: 'offsetBy',
  },
  REFERENCE: {
    START: 'start',
    END: 'end',
  },
};

// IMPORTANT: Any changes here must also update DEFAULT_DATE_RANGE in _shared_definition_constants.py.
export const DEFAULT_DATE_RANGE = {
  auto: {
    enabled: false,
    duration: moment.duration(1, 'days').valueOf(),
    offset: { value: 0, units: 'min' },
    offsetDirection: OFFSET_DIRECTION.PAST,
    cronSchedule: [QUARTZ_CRON_PENDING_INPUT],
    noCapsuleFound: false,
  },
  condition: {
    strategy: CAPSULE_SELECTION.STRATEGY.CLOSEST_TO,
    reference: CAPSULE_SELECTION.REFERENCE.END,
    offset: 1,
    range: {},
  },
  range: {},
  enabled: true,
};

// IMPORTANT: Any changes here must also update AG_GRID_AGGREGATION_FUNCTIONS in _shared_definition_constants.py.
export const AgGridAggregationFunctions = [
  'none',
  'sum',
  'min',
  'max',
  'count',
  'avg',
  'range',
  'stdDev',
  'first',
  'last',
] as const;
export type AgGridAggregationFunction = (typeof AgGridAggregationFunctions)[number];

// IMPORTANT: Any changes here must also update LABEL_PROPERTIES in _shared_definition_constants.py.
export enum LABEL_PROPERTIES {
  NAME = 'name',
  DESCRIPTION = 'description',
  ASSET = 'asset',
  LINE = 'line',
  ASSET_PATH_LEVELS = 'assetPathLevels',
  UOM = 'unitOfMeasure',
  CUSTOM = 'custom',
}

// IMPORTANT: Any changes here must also update LABEL_LOCATIONS in _shared_definition_constants.py.
export enum LABEL_LOCATIONS {
  OFF = 'off',
  LANE = 'lane',
  AXIS = 'axis',
}

// IMPORTANT: Any changes here must also update TREND_COLOR_MODE in _shared_definition_constants.py.
export enum TrendColorMode {
  Item = 'item',
  CapsuleProperty = 'capsuleProperty',
  CapsulePropertyGradient = 'capsulePropertyGradient',
}

// IMPORTANT: Any changes here must also update TABLE_BUILDER_MODE in _shared_definition_constants.py.
export enum TableBuilderMode {
  Simple = 'simple',
  Condition = 'condition',
}

// IMPORTANT: Any changes here must also update TABLE_BUILDER_HEADER_TYPE in _shared_definition_constants.py.
export enum TableBuilderHeaderType {
  None = 'none',
  Start = 'start',
  End = 'end',
  StartEnd = 'startEnd',
  CapsuleProperty = 'capsuleProperty',
}

// IMPORTANT: Any changes here must also update DASH_STYLES in _shared_definition_constants.py.
export enum DASH_STYLES {
  SOLID = 'Solid',
  SHORT_DASH = 'ShortDash',
  SHORT_DASH_DOT = 'ShortDashDot',
  SHORT_DASH_DOT_DOT = 'ShortDashDotDot',
  DOT = 'Dot',
  DASH = 'Dash',
  LONG_DASH = 'LongDash',
  DASH_DOT = 'DashDot',
  LONG_DASH_DOT = 'LongDashDot',
  LONG_DASH_DOT_DOT = 'LongDashDotDot',
}

// IMPORTANT: Any changes here must also update SAMPLE_OPTIONS in _shared_definition_constants.py.
export enum SAMPLE_OPTIONS {
  LINE = 'line',
  SAMPLES = 'sample',
  LINE_AND_SAMPLE = 'lineAndSample',
  BAR = 'bar',
}

// IMPORTANT: Any changes here must also update Y_AXIS_TYPES in _shared_definition_constants.py.
export enum Y_AXIS_TYPES {
  LOGARITHMIC = 'logarithmic',
  LINEAR = 'linear',
}

// IMPORTANT: Any changes here must also update WORKSHEET_VIEW in _shared_definition_constants.py.
// This is only the subset of worksheet.constants.ts's WORKSHEET_VIEW that SPy needs.
export const WORKSHEET_VIEW_KEYS = {
  TREND: 'TREND',
  SCATTER_PLOT: 'SCATTER_PLOT',
  TREEMAP: 'TREEMAP',
  TABLE: 'TABLE',
  ASSET_GROUP_EDITOR: 'ASSET_GROUP_EDITOR',
};

/**
 * Specifies the properties associated with a column in the series or capsule pane.
 */
export interface StatisticColumn extends BackwardsCompatibilityColumn {
  key: string;
  title: string;
  shortTitle: string;
  style: string;
  format?: string;
  stat: string;
  isStringCompatible?: boolean;
  prefix?: any;
  columnSuffix: string;
  headerName?: string;
  backingTableColumn?: string;
  backingTableUom?: string;
}

export const integerNumberFormat = '#,##0';

// IMPORTANT: Any changes here must also update get_trend_signal_statistic_function_map() in
// _shared_definition_constants.py.
export const TREND_SIGNAL_STATS: StatisticColumn[] = [
  {
    key: 'statistics.maximum',
    title: 'STATISTICS.MAXIMUM.LONG',
    shortTitle: 'STATISTICS.MAXIMUM.SHORT',
    style: 'decimal',
    stat: 'maxValue()',
    invalidsFirst: true,
    columnSuffix: 'Max',
    headerName: 'maximum',
  },
  {
    key: 'statistics.minimum',
    title: 'STATISTICS.MINIMUM.LONG',
    shortTitle: 'STATISTICS.MINIMUM.SHORT',
    style: 'decimal',
    stat: 'minValue()',
    invalidsFirst: true,
    columnSuffix: 'Min',
    headerName: 'minimum',
  },
  {
    key: 'statistics.range',
    title: 'STATISTICS.RANGE.LONG',
    shortTitle: 'STATISTICS.RANGE.SHORT',
    style: 'decimal',
    isStringCompatible: false,
    stat: 'range()',
    invalidsFirst: true,
    columnSuffix: 'Range',
    headerName: 'range',
  },
  {
    key: 'statistics.average',
    title: 'STATISTICS.AVERAGE.LONG',
    shortTitle: 'STATISTICS.AVERAGE.SHORT',
    style: 'decimal',
    stat: 'average()',
    invalidsFirst: true,
    columnSuffix: 'Average',
    headerName: 'average',
  },
  {
    key: 'statistics.stdDev',
    title: 'STATISTICS.STANDARD_DEVIATION.LONG',
    shortTitle: 'STATISTICS.STANDARD_DEVIATION.SHORT',
    style: 'decimal',
    stat: 'stdDev()',
    invalidsFirst: true,
    columnSuffix: 'StdDev',
    headerName: 'stdDev',
  },
  {
    key: 'statistics.percentGood',
    title: 'STATISTICS.PERCENT_GOOD.LONG',
    shortTitle: 'STATISTICS.PERCENT_GOOD.SHORT',
    style: 'percent',
    isStringCompatible: true,
    stat: 'percentGood()',
    invalidsFirst: true,
    columnSuffix: 'PercentGood',
    headerName: 'percentGood',
    backingTableUom: '%',
  },
  {
    key: 'statistics.count',
    title: 'STATISTICS.COUNT.LONG',
    shortTitle: 'STATISTICS.COUNT.SHORT',
    style: 'decimal',
    format: integerNumberFormat,
    isStringCompatible: true,
    stat: 'count()',
    invalidsFirst: true,
    columnSuffix: 'Count',
    headerName: 'count',
    backingTableUom: '',
  },
  {
    key: 'statistics.endValue',
    title: 'STATISTICS.LAST_VALUE.LONG',
    shortTitle: 'STATISTICS.LAST_VALUE.SHORT',
    style: 'decimal',
    isStringCompatible: true,
    stat: 'endValue(true)',
    invalidsFirst: true,
    columnSuffix: 'EndValue',
    headerName: 'endValue_true',
  },
  {
    key: 'statistics.delta',
    title: 'STATISTICS.DELTA.LONG',
    shortTitle: 'STATISTICS.DELTA.SHORT',
    style: 'decimal',
    isStringCompatible: false,
    stat: 'delta()',
    invalidsFirst: true,
    columnSuffix: 'Delta',
    headerName: 'delta',
  },
];
